/**
 * Notification Store - Global notification state management
 */

import { create } from 'zustand';
import { subscribeWithSelector, persist } from 'zustand/middleware';
import type {
  Notification,
  NotificationPreferences,
  NotificationStats,
  ToastNotification,
  NotificationType,
  NotificationPriority,
} from './types';
import { supabase } from '../../lib/supabase';

interface NotificationState {
  // Notifications
  notifications: Notification[];
  toasts: ToastNotification[];
  stats: NotificationStats | null;
  isLoading: boolean;
  error: string | null;

  // Preferences
  preferences: NotificationPreferences | null;

  // Browser permission
  browserPermission: NotificationPermission;

  // Sound
  soundEnabled: boolean;
  soundVolume: number;

  // Panel state
  isPanelOpen: boolean;

  // Actions
  fetchNotifications: (userId: string) => Promise<void>;
  addNotification: (notification: Omit<Notification, 'id' | 'createdAt'>) => void;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  dismissNotification: (notificationId: string) => Promise<void>;
  clearAll: () => Promise<void>;

  // Toast actions
  showToast: (toast: Omit<ToastNotification, 'id'>) => string;
  dismissToast: (toastId: string) => void;

  // Preferences
  fetchPreferences: (userId: string) => Promise<void>;
  updatePreferences: (preferences: Partial<NotificationPreferences>) => Promise<void>;

  // Browser notifications
  requestBrowserPermission: () => Promise<boolean>;
  showBrowserNotification: (notification: Notification) => void;

  // Sound
  playNotificationSound: (type?: NotificationType) => void;
  setSoundEnabled: (enabled: boolean) => void;
  setSoundVolume: (volume: number) => void;

  // Slack/Teams
  sendToSlack: (notification: Notification) => Promise<void>;
  sendToTeams: (notification: Notification) => Promise<void>;

  // Panel
  togglePanel: () => void;
  closePanel: () => void;
}

// Sound files (would be loaded from public folder)
const NOTIFICATION_SOUNDS: Record<string, string> = {
  default: '/sounds/notification.mp3',
  chime: '/sounds/chime.mp3',
  bell: '/sounds/bell.mp3',
  alert: '/sounds/alert.mp3',
};

// Audio element for playing sounds
let audioElement: HTMLAudioElement | null = null;

const initAudio = () => {
  if (typeof window !== 'undefined' && !audioElement) {
    audioElement = new Audio();
    audioElement.volume = 0.5;
  }
  return audioElement;
};

const mapQueueRowToNotification = (n: any): Notification => {
  const channels = Array.isArray(n.channels)
    ? n.channels
    : n.channel
      ? [n.channel]
      : ['in_app'];
  const actionUrl = n.action_url || n.data?.action_url;

  return {
    id: n.id,
    userId: n.recipient_id || n.user_id,
    type: n.type || (n.category === 'codex_software_update' ? 'system' : 'info'),
    priority: n.priority || 'normal',
    title: n.title || 'Platform notification',
    message: n.message,
    data: n.data || n.context || {},
    actions: actionUrl
      ? [{
          id: 'open',
          label: 'Open',
          action: actionUrl,
          primary: true,
        }]
      : [],
    channels,
    read: Boolean(n.read_at || n.read),
    dismissed: Boolean(n.dismissed_at || n.dismissed),
    createdAt: n.created_at,
    expiresAt: n.expires_at,
    readAt: n.read_at,
    clickedAt: n.clicked_at,
  };
};

export const useNotificationStore = create<NotificationState>()(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        // Initial state
        notifications: [],
        toasts: [],
        stats: null,
        isLoading: false,
        error: null,
        preferences: null,
        browserPermission: typeof Notification !== 'undefined'
          ? Notification.permission
          : 'denied',
        soundEnabled: true,
        soundVolume: 50,
        isPanelOpen: false,

        // Fetch notifications from database
        fetchNotifications: async (userId: string) => {
          set({ isLoading: true, error: null });

          try {
            const { data, error } = await supabase
              .from('notification_queue')
              .select('*')
              .eq('recipient_id', userId)
              .is('dismissed_at', null)
              .order('created_at', { ascending: false })
              .limit(100);

            if (error) throw error;

            const notifications: Notification[] = (data || []).map(mapQueueRowToNotification);

            // Calculate stats
            const stats: NotificationStats = {
              total: notifications.length,
              unread: notifications.filter((n) => !n.read).length,
              byType: {} as Record<NotificationType, number>,
              byPriority: {} as Record<NotificationPriority, number>,
            };

            notifications.forEach((n) => {
              stats.byType[n.type] = (stats.byType[n.type] || 0) + 1;
              stats.byPriority[n.priority] = (stats.byPriority[n.priority] || 0) + 1;
            });

            set({ notifications, stats, isLoading: false });
          } catch (err) {
            set({
              error: err instanceof Error ? err.message : 'Failed to fetch notifications',
              isLoading: false,
            });
          }
        },

        // Add a new notification
        addNotification: (notification) => {
          const { preferences, soundEnabled, showBrowserNotification, playNotificationSound, sendToSlack, sendToTeams } = get();

          const newNotification: Notification = {
            ...notification,
            id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            createdAt: new Date().toISOString(),
            read: false,
            dismissed: false,
          };

          set((state) => ({
            notifications: [newNotification, ...state.notifications],
            stats: state.stats
              ? {
                  ...state.stats,
                  total: state.stats.total + 1,
                  unread: state.stats.unread + 1,
                }
              : null,
          }));

          // Handle channels
          if (notification.channels.includes('browser')) {
            showBrowserNotification(newNotification);
          }
          if (notification.channels.includes('sound') && soundEnabled) {
            playNotificationSound(notification.type);
          }
          if (notification.channels.includes('slack') && preferences?.slackWebhook) {
            sendToSlack(newNotification);
          }
          if (notification.channels.includes('teams') && preferences?.teamsWebhook) {
            sendToTeams(newNotification);
          }

          // Save to database
          supabase
            .from('notification_queue')
            .insert({
              recipient_type: 'user',
              recipient_id: notification.userId,
              channel: notification.channels[0] || 'in_app',
              priority: notification.priority,
              title: notification.title,
              message: notification.message,
              data: notification.data,
              channels: notification.channels,
              action_url: notification.actions?.[0]?.action,
              category: notification.type === 'system' ? 'system' : notification.type,
              status: 'pending',
            })
            .select()
            .then(({ data, error }) => {
              if (!error && data?.[0]) {
                // Update with real ID
                set((state) => ({
                  notifications: state.notifications.map((n) =>
                    n.id === newNotification.id ? mapQueueRowToNotification(data[0]) : n
                  ),
                }));
              }
            });
        },

        // Mark notification as read
        markAsRead: async (notificationId: string) => {
          const notification = get().notifications.find((n) => n.id === notificationId);
          if (!notification || notification.read) return;

          const { data, error } = await supabase.rpc('ops_notification_action', {
            p_notification_id: notificationId,
            p_action: 'read',
          });
          if (error || !data?.ok) {
            set({ error: error?.message || 'Failed to mark notification as read' });
            return;
          }

          set((state) => ({
            notifications: state.notifications.map((n) =>
              n.id === notificationId ? { ...n, read: true, readAt: new Date().toISOString() } : n
            ),
            stats: state.stats
              ? { ...state.stats, unread: Math.max(0, state.stats.unread - 1) }
              : null,
            error: null,
          }));
        },

        // Mark all as read
        markAllAsRead: async () => {
          const { data, error } = await supabase.rpc('ops_notification_bulk_action', {
            p_action: 'read_all',
          });
          if (error || !data?.ok) {
            set({ error: error?.message || 'Failed to mark notifications as read' });
            return;
          }

          set((state) => ({
            notifications: state.notifications.map((n) => ({
              ...n,
              read: true,
              readAt: new Date().toISOString(),
            })),
            stats: state.stats ? { ...state.stats, unread: 0 } : null,
            error: null,
          }));
        },

        // Dismiss notification
        dismissNotification: async (notificationId: string) => {
          const { data, error } = await supabase.rpc('ops_notification_action', {
            p_notification_id: notificationId,
            p_action: 'dismiss',
          });
          if (error || !data?.ok) {
            set({ error: error?.message || 'Failed to dismiss notification' });
            return;
          }

          set((state) => ({
            notifications: state.notifications.filter((n) => n.id !== notificationId),
            stats: state.stats
              ? {
                  ...state.stats,
                  total: state.stats.total - 1,
                  unread: state.notifications.find((n) => n.id === notificationId && !n.read)
                    ? state.stats.unread - 1
                    : state.stats.unread,
                  }
              : null,
            error: null,
          }));
        },

        // Clear all notifications
        clearAll: async () => {
          const { data, error } = await supabase.rpc('ops_notification_bulk_action', {
            p_action: 'dismiss_all',
          });
          if (error || !data?.ok) {
            set({ error: error?.message || 'Failed to clear notifications' });
            return;
          }

          set({
            notifications: [],
            stats: { total: 0, unread: 0, byType: {} as any, byPriority: {} as any },
            error: null,
          });
        },

        // Show toast
        showToast: (toast) => {
          const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          const newToast: ToastNotification = { ...toast, id };

          set((state) => ({
            toasts: [...state.toasts, newToast],
          }));

          // Auto-dismiss after duration
          const duration = toast.duration || 5000;
          if (duration > 0) {
            setTimeout(() => {
              get().dismissToast(id);
            }, duration);
          }

          return id;
        },

        // Dismiss toast
        dismissToast: (toastId: string) => {
          set((state) => ({
            toasts: state.toasts.filter((t) => t.id !== toastId),
          }));
        },

        // Fetch preferences
        fetchPreferences: async (userId: string) => {
          try {
            const { data, error } = await supabase
              .from('notification_preferences')
              .select('*')
              .eq('user_id', userId)
              .single();

            if (error && error.code !== 'PGRST116') throw error;

            if (data) {
              set({ preferences: data as any });
            }
          } catch (err) {
            console.error('Error fetching notification preferences:', err);
          }
        },

        // Update preferences
        updatePreferences: async (preferences: Partial<NotificationPreferences>) => {
          set((state) => ({
            preferences: state.preferences
              ? { ...state.preferences, ...preferences }
              : null,
          }));

          const { preferences: current } = get();
          if (current) {
            await supabase
              .from('notification_preferences')
              .upsert({
                user_id: current.userId,
                ...preferences,
              });
          }
        },

        // Request browser permission
        requestBrowserPermission: async () => {
          if (typeof Notification === 'undefined') {
            return false;
          }

          if (Notification.permission === 'granted') {
            set({ browserPermission: 'granted' });
            return true;
          }

          if (Notification.permission === 'denied') {
            set({ browserPermission: 'denied' });
            return false;
          }

          try {
            const permission = await Notification.requestPermission();
            set({ browserPermission: permission });
            return permission === 'granted';
          } catch (err) {
            return false;
          }
        },

        // Show browser notification
        showBrowserNotification: (notification: Notification) => {
          const { browserPermission } = get();

          if (browserPermission !== 'granted') return;

          try {
            const browserNotif = new Notification(notification.title, {
              body: notification.message,
              icon: '/favicon.ico',
              badge: '/favicon.ico',
              tag: notification.id,
              data: notification.data,
            });

            browserNotif.onclick = () => {
              window.focus();
              get().markAsRead(notification.id);
            };
          } catch (err) {
            console.error('Error showing browser notification:', err);
          }
        },

        // Play notification sound
        playNotificationSound: (type?: NotificationType) => {
          const { soundEnabled, soundVolume, preferences } = get();
          if (!soundEnabled) return;

          const audio = initAudio();
          if (!audio) return;

          const soundType = preferences?.soundType || 'default';
          if (soundType === 'none') return;

          audio.src = NOTIFICATION_SOUNDS[soundType] || NOTIFICATION_SOUNDS.default;
          audio.volume = soundVolume / 100;

          audio.play().catch((err) => {
            console.error('Error playing notification sound:', err);
          });
        },

        // Sound settings
        setSoundEnabled: (enabled: boolean) => set({ soundEnabled: enabled }),
        setSoundVolume: (volume: number) => set({ soundVolume: Math.max(0, Math.min(100, volume)) }),

        // Send to Slack
        sendToSlack: async (notification: Notification) => {
          const { preferences } = get();
          if (!preferences?.slackWebhook) return;

          try {
            const colorMap: Record<NotificationPriority, string> = {
              low: '#36a64f',
              normal: '#2eb886',
              high: '#ECB22E',
              urgent: '#E01E5A',
            };

            const payload = {
              text: notification.title,
              attachments: [
                {
                  color: colorMap[notification.priority] || colorMap.normal,
                  title: notification.title,
                  text: notification.message,
                  fields: notification.data
                    ? Object.entries(notification.data).map(([key, value]) => ({
                        title: key,
                        value: String(value),
                        short: true,
                      }))
                    : undefined,
                  footer: 'PropellerTravel TMC Platform',
                  ts: Math.floor(Date.now() / 1000),
                },
              ],
            };

            await fetch(preferences.slackWebhook, {
              method: 'POST',
              body: JSON.stringify(payload),
            });
          } catch (err) {
            console.error('Error sending Slack notification:', err);
          }
        },

        // Send to Teams
        sendToTeams: async (notification: Notification) => {
          const { preferences } = get();
          if (!preferences?.teamsWebhook) return;

          try {
            const colorMap: Record<NotificationPriority, string> = {
              low: '36a64f',
              normal: '2eb886',
              high: 'ECB22E',
              urgent: 'E01E5A',
            };

            const payload = {
              '@type': 'MessageCard',
              '@context': 'http://schema.org/extensions',
              themeColor: colorMap[notification.priority] || colorMap.normal,
              summary: notification.title,
              sections: [
                {
                  activityTitle: notification.title,
                  text: notification.message,
                  facts: notification.data
                    ? Object.entries(notification.data).map(([name, value]) => ({
                        name,
                        value: String(value),
                      }))
                    : undefined,
                },
              ],
            };

            await fetch(preferences.teamsWebhook, {
              method: 'POST',
              body: JSON.stringify(payload),
              headers: { 'Content-Type': 'application/json' },
            });
          } catch (err) {
            console.error('Error sending Teams notification:', err);
          }
        },

        // Panel
        togglePanel: () => set((state) => ({ isPanelOpen: !state.isPanelOpen })),
        closePanel: () => set({ isPanelOpen: false }),
      }),
      {
        name: 'notification-preferences',
        partialize: (state) => ({
          soundEnabled: state.soundEnabled,
          soundVolume: state.soundVolume,
        }),
      }
    )
  )
);

// Selectors
export const selectUnreadCount = (state: NotificationState) =>
  state.stats?.unread || 0;

export const selectNotificationsByType = (state: NotificationState, type: NotificationType) =>
  state.notifications.filter((n) => n.type === type);

export const selectHighPriorityNotifications = (state: NotificationState) =>
  state.notifications.filter((n) => n.priority === 'high' || n.priority === 'urgent');
